import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProperties,
  getProperty,
  createProperty,
  updateProperty,
  deleteProperty,
  getPropertiesByAgent,
  getPropertyByAgent,
  togglePropertyStatus,
} from "../../api/properties/property.api.js";

export const usePropertiesQuery = () => {
  return useQuery({
    queryKey: ["properties"],
    queryFn: getProperties,
  });
};

export const usePropertiesByAgentQuery = (agentId) => {
  return useQuery({
    queryKey: ["properties", agentId],
    queryFn: () => getPropertiesByAgent(agentId),
    enabled: !!agentId,
    // Avoid refetching repeatedly on mount/focus (React Query defaults to refetch on mount
    // and on window focus). This makes the API call happen only once per page load,
    // and keeps the data cached for a short while to avoid duplicate calls in dev/StrictMode.
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const usePropertyQuery = (propertyId) => {
  return useQuery({
    queryKey: ["properties", propertyId],
    queryFn: () => getProperty(propertyId),
    enabled: !!propertyId,
  });
};

export const usePropertyByAgentQuery = (agentId, propertyId) => {
  return useQuery({
    queryKey: ["properties", agentId, propertyId],
    queryFn: () => getPropertyByAgent(agentId, propertyId),
    enabled: !!agentId && !!propertyId,
  });
};

export const useCreatePropertyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }) => createProperty(agentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", variables.agentId],
      });
    },
  });
};

export const useUpdatePropertyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, propertyId, data }) =>
      updateProperty(agentId, propertyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", variables.agentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", variables.agentId, variables.propertyId],
      });
    },
  });
};

export const useDeletePropertyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, propertyId }) =>
      deleteProperty(agentId, propertyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", variables.agentId],
      });
    },
  });
};

export const useTogglePropertyStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, propertyId }) =>
      togglePropertyStatus(agentId, propertyId),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });

      queryClient.invalidateQueries({
        queryKey: ["properties", variables.agentId],
      });
    },
  });
};
